import { useEffect, useState } from "react";
import { accountApi } from "../../services/accountApi";
import { useToast } from "../../context/ToastContext";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { Badge } from "../../components/common/Badge";
import { DataTable } from "../../components/common/DataTable";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { formatDateOnly } from "../../services/hiveActions";

/**
 * Take your data with you, or ask for it to be deleted (#284).
 *
 * POST /account/gdpr-request and GET /account/gdpr-requests have existed
 * since the account work, and the admin console has had a queue for
 * REVIEWING requests the whole time. Nothing could make one: the
 * pre-prod sweep for routes with no client caller found both endpoints
 * unreferenced anywhere in web or mobile.
 *
 * That is not a missing nicety. Right of access and right of erasure are
 * obligations, not features, and an app that offers neither is not ready
 * to take a paying customer in the EU, the UK or California.
 *
 * THE TWO HALVES BEHAVE COMPLETELY DIFFERENTLY, and the screen has to
 * say so rather than presenting two similar-looking buttons:
 *
 *   Export is synchronous. The server assembles the bundle and returns
 *   it in the response body, so there is nothing to wait for and nothing
 *   to come back for.
 *
 *   Deletion is a REQUEST. It goes into a queue for an admin, because an
 *   account can own hives and records that a team still relies on --
 *   deleting one person's row can take somebody else's season with it.
 *   Presenting that as an instant button would be a promise the system
 *   deliberately does not keep.
 */

const STATUS_TONE = {
  Completed: "success",
  Pending: "warning",
  Approved: "info",
  Rejected: "danger",
};

export function YourDataTab() {
  const { notify } = useToast();
  const [requests, setRequests] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // The pending-deletion message from the server, or null. Kept in state
  // rather than only toasted: it explains WHY nothing happened
  // immediately, and a toast that has faded cannot answer that.
  const [deletionNote, setDeletionNote] = useState(null);

  function load() {
    accountApi.dataRequests()
      .then((r) => setRequests(r.requests || []))
      // An empty table would read as "you have never asked for anything",
      // which is a different statement from "we could not check".
      .catch(() => setRequests([]));
  }
  useEffect(() => { load(); }, []);

  async function exportData() {
    setBusy(true);
    try {
      const result = await accountApi.requestDataExport();
      downloadBundle(result.data);
      notify("Your data has been downloaded", "success");
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not export your data", "danger");
    } finally {
      setBusy(false);
    }
  }

  /*
   * Saved from the response the browser already has, NOT fetched again
   * from a URL. The bundle contains everything the account holds, so
   * putting it behind a link would mean either a second request that
   * re-assembles it or a temporary file on a server -- one more copy of
   * somebody's whole business, sitting somewhere, for no benefit.
   *
   * revokeObjectURL in a finally: an un-revoked blob URL pins the entire
   * bundle in memory for the life of the tab.
   */
  function downloadBundle(bundle) {
    const stamp = new Date().toISOString().slice(0, 10);
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" }));
    try {
      const a = document.createElement("a");
      a.href = url;
      a.download = `apiary-data-${stamp}.json`;
      a.click();
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  async function requestDeletion() {
    setBusy(true);
    try {
      const result = await accountApi.requestAccountDeletion();
      setConfirmDelete(false);
      setDeletionNote(result.message || "Your request has been recorded.");
      notify("Deletion request submitted", "success");
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not submit that request", "danger");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <Card title="Download your data" className="section-spacing">
        {/*
            This paragraph was written from what an export OUGHT to
            contain rather than from reading the function, and it was
            wrong: the bundle held account administration only -- profile,
            sessions, subscription changes, an audit trail -- and none of
            the hives, harvests or vendors it claimed. Reported from dev
            as "mostly just the computer sessions I've logged in from and
            a change log".

            The bundle now contains what this says. The lesson is the
            older one, which is that copy describing a payload has to be
            checked against the payload, not against the intention.
         */}
        <p style={{ marginTop: 0 }}>
          Everything this account holds — your sites and hives, inspections,
          treatments and feedings, harvests, honey movements, queens, your
          vendors and expenditures, your journal, and your account history —
          as a single JSON file.
        </p>
        <p style={{ color: "var(--color-text-muted)" }}>
          It downloads straight away. Nothing is emailed and no copy is kept
          on our side.
        </p>
        <p style={{ color: "var(--color-text-muted)" }}>
          {/* Said here as well as in the file, because a member's export
              is legitimately thin and a thin file with no explanation
              reads as a broken one. */}
          Hives belonging to another beekeeper you help are theirs, so they
          are not in your file.
        </p>
        <Button onClick={exportData} disabled={busy}>
          {busy ? "Preparing…" : "Download my data"}
        </Button>
      </Card>

      <Card title="Delete this account" className="section-spacing">
        <p style={{ marginTop: 0 }}>
          You can ask for this account and its data to be deleted. This one
          is not instant, and the reason matters:{" "}
          <strong>an account can own hives and records that other people on
          your team still use.</strong> Somebody checks that before anything
          is removed, so a shared season is not deleted along with it.
        </p>
        {deletionNote ? (
          <p style={{ color: "var(--color-text-muted)" }}>{deletionNote}</p>
        ) : null}
        {/* Danger, and nowhere near the primary above it -- rule 4. */}
        <Button variant="danger" disabled={busy} onClick={() => setConfirmDelete(true)}>
          Request account deletion
        </Button>
      </Card>

      <Card title="Your requests">
        <DataTable
          rows={requests}
          loading={requests === null}
          rowKey="request_id"
          emptyTitle="No requests yet"
          emptyDescription="Exports and deletion requests you make will be listed here."
          columns={[
            { key: "request_type", header: "Request" },
            {
              key: "status",
              header: "Status",
              render: (r) => <Badge tone={STATUS_TONE[r.status] || "neutral"}>{r.status}</Badge>,
            },
            {
              key: "requested_date",
              header: "Asked",
              render: (r) => formatDateOnly(r.requested_date),
            },
            {
              key: "completed_date",
              header: "Answered",
              render: (r) => (r.completed_date ? formatDateOnly(r.completed_date) : "—"),
            },
            { key: "notes", header: "Notes", render: (r) => r.notes || "—" },
          ]}
        />
      </Card>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={requestDeletion}
        title="Request deletion of this account?"
        // Differs from the button that opened it (rule 4), and says what
        // the action actually IS -- this submits a request, it does not
        // delete anything, and a confirm reading "Delete" would promise
        // otherwise.
        confirmLabel="Submit request"
        danger
        busy={busy}
      >
        <p>
          This asks for your account and its data to be deleted. It is
          reviewed first, because your account may own hives or records
          your team still relies on — you&apos;ll be told once it has been
          dealt with.
        </p>
        <p>
          If you want a copy of anything, download your data before you
          submit this.
        </p>
      </ConfirmDialog>
    </div>
  );
}
